<template>
  <div class="field-order" :class="{ 'field-order--with-bar': hasBar }">
    <v-alert v-if="error && !order" type="error" dense outlined>{{ error }}</v-alert>
    <div v-if="!order && !error" class="text-center py-10"><v-progress-circular indeterminate color="primary" /></div>

    <!-- Pose terminée : on le dit en grand, et on enchaîne. -->
    <div v-if="order && order.status === 'in-service'" class="text-center py-8">
      <v-icon size="96" color="success">mdi-check-circle</v-icon>
      <div class="text-h5 font-weight-bold mt-3">{{ $t('onboarding.field.done-title') }}</div>
      <div class="text-body-1 mt-1">{{ beneficiaryOf(order) }}</div>
      <div v-if="order.equipmentLabel" class="text-body-2 text--secondary">{{ order.equipmentLabel }}</div>
      <div class="text-body-2 text--secondary mt-1">{{ $t('onboarding.field.done') }}</div>
      <v-alert v-if="error" type="error" dense text class="mt-6 text-left">{{ error }}</v-alert>
      <v-btn block depressed height="40" color="primary" class="mt-8" :loading="working" @click="next">
        {{ $t('onboarding.field.next') }}<v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
      <v-btn block small text class="mt-2" :to="{ name: 'field' }" exact>{{ $t('onboarding.field.mine') }}</v-btn>
    </div>

    <template v-else-if="order">
      <!-- Quand, chez qui, où, qui appeler : l'essentiel en haut, un doigt pour chaque geste. -->
      <v-card class="mb-4">
        <v-card-text class="pa-4">
          <div class="d-flex align-center mb-2">
            <v-icon small class="mr-1">{{ kindIcon(order.kind) }}</v-icon>
            <span class="text-body-2">{{ kindLabel(order.kind) }}</span>
            <v-spacer />
            <v-chip v-if="order.installation" small :color="installationColor" dark>{{ $t(`onboarding.commissioning.installation.${order.installation}`) }}</v-chip>
          </div>
          <div class="text-h6 text--primary">{{ beneficiaryOf(order) }}</div>
          <div v-if="order.holder === 'member' && order.organizationName" class="text-body-2 text--secondary">{{ order.organizationName }}</div>

          <div class="field-order__lines mt-3 text-body-2">
            <div :class="{ 'red--text text--darken-1': dayOf(order) === 'late' }">
              <v-icon small class="mr-2" :color="dayOf(order) === 'late' ? 'red darken-1' : undefined">mdi-calendar-clock</v-icon>
              <template v-if="order.scheduledAt">{{ order.scheduledAt | formatDate('dddd DD/MM [·] HH[h]mm') }}</template>
              <span v-else class="text--secondary">{{ $t('onboarding.field.no-date') }}</span>
            </div>
            <div><v-icon small class="mr-2">mdi-map-marker-outline</v-icon>{{ addressOf(order) }}</div>
            <!-- Qui joindre sur place, et son numéro en clair : on le lit, on l'appelle. -->
            <div v-if="contact">
              <v-icon small class="mr-2">mdi-account-outline</v-icon>{{ contact.name || $t('onboarding.field.contact') }}
            </div>
            <div v-if="phone">
              <v-icon small class="mr-2">mdi-phone-outline</v-icon><a :href="`tel:${phone}`">{{ phone }}</a>
            </div>
          </div>

          <div class="field-order__quick mt-3">
            <v-btn small outlined color="primary" :disabled="!routeUrl" :href="routeUrl" target="_blank" rel="noopener">
              <v-icon left small>mdi-directions</v-icon>{{ $t('onboarding.field.route') }}
            </v-btn>
            <v-btn small outlined color="primary" :disabled="!phone" :href="phone ? `tel:${phone}` : undefined">
              <v-icon left small>mdi-phone</v-icon>{{ $t('onboarding.field.call') }}
            </v-btn>
            <v-btn small outlined color="primary" :disabled="!phone" :href="phone ? `sms:${phone}` : undefined">
              <v-icon left small>mdi-message-text-outline</v-icon>{{ $t('onboarding.field.sms') }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <v-alert v-if="order.status === 'cancelled' || order.status === 'rejected'" type="info" text>{{ $t('onboarding.field.cancelled') }}</v-alert>

      <template v-else>
        <!-- Ce qui empêche d'avancer, dit avant le reste. -->
        <v-alert v-if="takenByOther" type="info" text dense>{{ $t('onboarding.field.taken-by', { name: order.technician.name }) }}</v-alert>
        <v-alert v-if="!order.readyForInstallation && !plan" type="warning" text dense>
          <div class="font-weight-bold">{{ $t('onboarding.field.blocked.title') }}</div>
          <ul v-if="(order.installationBlockers || []).length" class="pl-4 mb-1">
            <li v-for="requirement in order.installationBlockers" :key="requirement">{{ fieldRequirementText('blocked', requirement) }}</li>
          </ul>
          <div v-else>{{ $t('onboarding.field.blocked.not-approved') }}</div>
          <div class="mb-2">{{ $t('onboarding.field.blocked.call') }}</div>
          <field-supervision-button small :contact="order.supervision" />
        </v-alert>
        <!-- Ce qui manque à la personne n'empêche pas de poser : il est dit, sans plus. -->
        <v-alert v-if="(order.notBlockingRequirements || []).length && !plan && isOpen" type="info" text dense>
          <div class="font-weight-bold">{{ $t(order.readyForInstallation ? 'onboarding.field.not-blocking.title' : 'onboarding.field.not-blocking.title-also') }}</div>
          <ul class="pl-4">
            <li v-for="requirement in order.notBlockingRequirements" :key="requirement">{{ fieldRequirementText('not-blocking', requirement) }}</li>
          </ul>
        </v-alert>

        <field-briefing :order="order" class="mb-4" />

        <!-- La pose elle-même : libre et prête, ou la sienne. Rien à prendre avant : la mise en service la lui donne. -->
        <template v-if="installing">
          <!-- L'emplacement : connu dès la demande ; on ne le saisit ici que s'il manque ou s'il est faux. -->
          <v-card class="mb-4">
            <v-card-text class="pa-4">
              <div class="field-order__step">
                <v-icon class="mr-2" :color="locationReady ? 'success' : 'primary'">{{ locationReady ? 'mdi-check-circle' : 'mdi-map-marker' }}</v-icon>
                {{ $t('onboarding.field.step-location') }}
              </div>
              <template v-if="plannedLocation && !correcting">
                <p class="text-body-2 mb-1">{{ plannedLocation }}</p>
                <v-btn x-small text color="primary" class="px-0 text-none" :disabled="!!plan" @click="correcting = true">{{ $t('onboarding.field.location-wrong') }}</v-btn>
              </template>
              <template v-else>
                <p class="text-body-2 text--secondary">{{ $t(plannedLocation ? 'onboarding.field.location-correct-hint' : (order.locationChoice === 'at-installation' ? 'onboarding.field.location-at-installation-hint' : 'onboarding.field.location-missing-hint')) }}</p>
                <!-- Un autre emplacement de l'organisation, ou une nouvelle adresse : le technicien pose où il est. -->
                <v-autocomplete
                    v-if="sites.length"
                    :value="newPlace && newPlace.locationId"
                    :items="sites" item-value="id" :item-text="siteText"
                    :label="$t('onboarding.field.location-site')"
                    outlined dense clearable hide-details class="mb-3"
                    @change="chooseSite"
                />
                <div v-if="sites.length && !(newPlace && newPlace.locationId)" class="text-caption text--secondary mb-2">{{ $t('onboarding.field.location-or-new') }}</div>
                <new-location-picker v-if="!(newPlace && newPlace.locationId && isSite(newPlace.locationId))" v-model="newPlace" :organization-id="order.organizationId" :label="$t('onboarding.commissioning.form.address')" required />
                <v-btn v-if="plannedLocation" x-small text class="px-0 text-none" @click="correcting = false; newPlace = null">{{ $t('onboarding.field.location-keep') }}</v-btn>
              </template>
            </v-card-text>
          </v-card>

          <!-- L'équipement : désigné (saisi ou préparé), vérifié, puis mis en service. -->
          <v-card class="mb-4" :disabled="!locationReady">
            <v-card-text class="pa-4">
              <div class="field-order__step">
                <v-icon class="mr-2" color="primary">{{ kindIcon(order.kind) }}</v-icon>
                {{ $t(`onboarding.field.step-equipment-${order.kind}`) }}
              </div>

              <template v-if="!preview && !plan">
                <!-- Un compteur préparé se pose tel quel ; une borne peut toujours être désignée sur place. -->
                <p v-if="!isChargingPoint && order.preparedEquipmentId" class="text-body-1 mb-0">{{ $t('onboarding.field.prepared', { equipment: order.equipmentLabel || '' }) }}</p>
                <v-text-field
                    v-else
                    ref="identifier"
                    v-model="identifier"
                    :label="$t(isChargingPoint ? 'onboarding.commissioning.activation.identifier' : 'onboarding.field.meter-reference')"
                    :hint="order.preparedEquipmentId ? $t('onboarding.field.identifier-optional') : $t(isChargingPoint ? 'onboarding.field.identifier-hint' : 'onboarding.field.meter-reference-hint')"
                    persistent-hint
                    outlined
                    clearable
                    autocapitalize="off" autocorrect="off" spellcheck="false"
                    class="field-order__identifier"
                    @keyup.enter="check"
                />
              </template>

              <template v-else-if="preview && !plan">
                <v-alert type="info" outlined dense>
                  <div class="font-weight-bold">{{ preview.equipment.identifier || preview.equipment.name }}<span v-if="preview.equipment.identifier && preview.equipment.name && preview.equipment.name !== preview.equipment.identifier"> · {{ preview.equipment.name }}</span></div>
                  <div class="text-caption">
                    {{ $t('onboarding.commissioning.activation.currently', {
                      status: chargingPointStatusLabel(preview.equipment.status),
                      location: preview.equipment.locationName || $t('onboarding.commissioning.activation.no-location'),
                    }) }}
                  </div>
                </v-alert>
                <!-- Déjà affectée ailleurs : on ne bloque pas (il s'est déplacé), on dit ce qu'elle quitte, il confirme. -->
                <reassignment-warning v-if="preview.reassignment" v-model="reassignmentConfirmed" :reassignment="preview.reassignment" />
                <!-- D2 : ce qui désigne chaque point de charge sur place (n° de place, étage, étiquette) ; facultatif. -->
                <template v-if="isChargingPoint && (preview.equipment.evses || []).length">
                  <div class="text-subtitle-2 mb-1">{{ $t('onboarding.field.evse-references.title') }}</div>
                  <p class="text-caption text--secondary mb-2">{{ $t('onboarding.field.evse-references.hint') }}</p>
                  <v-text-field
                      v-for="evse in preview.equipment.evses" :key="evse.id"
                      v-model="evseReferences[evse.id]"
                      :label="$t('onboarding.field.evse-references.label', { num: evse.num })"
                      outlined dense hide-details maxlength="64" class="mb-3"
                  />
                </template>
                <div class="text-subtitle-2 mb-1">{{ $t('onboarding.field.what-happens') }}</div>
                <plan-steps :steps="preview.plan.steps" class="mb-2" />
                <v-btn small text color="primary" class="px-0 text-none" :disabled="working" @click="preview = null">{{ $t('onboarding.field.other-equipment') }}</v-btn>
              </template>

              <template v-else>
                <!-- Arrêté en route : l'étape et sa raison, en clair, avant le détail. -->
                <v-alert v-if="failedStep" type="error" text dense>
                  <div class="font-weight-medium">{{ $t('onboarding.field.failed-at', { step: stepLabel(failedStep) }) }}</div>
                  <div v-if="failedStep.reason">{{ failedStep.reasonLabel || translated(failedStep.reason, failedStep.reasonParameters) }}</div>
                </v-alert>
                <plan-steps :steps="plan.steps" />
              </template>
            </v-card-text>
          </v-card>
        </template>
      </template>

      <!-- La barre d'action : le geste suivant, en gros, sous le pouce ; « Un problème ? » à côté. -->
      <div v-if="hasBar" class="field-order__bar elevation-8">
        <div class="field-order__bar-inner">
          <v-alert v-if="error" type="error" dense text class="mb-2 text-body-2">{{ error }}</v-alert>
          <div class="d-flex align-center">
            <v-btn v-if="canSignal" outlined color="error" height="40" class="mr-2 field-order__problem" :disabled="working" :title="$t('onboarding.field.issue.menu')" @click="issueOpen = true">
              <v-icon small>mdi-alert-outline</v-icon>
            </v-btn>
            <v-btn v-if="action" depressed color="primary" height="40" class="field-order__main text-none" :loading="working" :disabled="action.disabled" @click="action.run">
              <v-icon left small>{{ action.icon }}</v-icon>{{ $t(`onboarding.field.action.${action.key}`) }}
            </v-btn>
          </div>
        </div>
      </div>

      <field-issue-sheet v-if="canSignal" v-model="issueOpen" :order="order" :mine="takenByMe" @done="leave" />
    </template>
  </div>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'
import NewLocationPicker from '../NewLocationPicker.vue'
import PlanSteps from '../PlanSteps.vue'
import FieldSupervisionButton from './FieldSupervisionButton.vue'
import { planStepName } from '../planLabels'
import ReassignmentWarning from '../ReassignmentWarning.vue'
import FieldMixin from './FieldMixin'
import FieldBriefing from './FieldBriefing.vue'
import FieldIssueSheet from './FieldIssueSheet.vue'

/**
 * Une pose sur le terrain. En haut : quand, chez qui, où, qui appeler ; puis le brief. En bas, une
 * barre fixe porte le geste suivant : vérifier l'équipement, mettre en
 * service, réessayer. « Un problème ? » est toujours à côté : signaler, rendre, appeler.
 *
 * Un sous-sol coupe la 4G au moment précis où l'on saisit : l'identifiant reste sur l'appareil tant
 * que la pose n'est pas faite, et relancer ne double rien (chaque geste se reconnaît déjà fait).
 */
export default {
  name: 'FieldOrder',

  components: { FieldSupervisionButton, NewLocationPicker, PlanSteps, ReassignmentWarning, FieldBriefing, FieldIssueSheet },

  mixins: [CommissioningMixin, FieldMixin],

  data() {
    return {
      order: null,
      plan: null,
      preview: null,
      // D2 : l'identifiant de chaque point de charge → ce qui le désigne sur place.
      evseReferences: {},
      // Les emplacements de l'organisation (ses sites), au choix sur place.
      sites: [],
      identifier: '',
      newPlace: null,
      correcting: false,
      working: false,
      error: null,
      issueOpen: false,
      reassignmentConfirmed: false,
    }
  },

  computed: {
    storageKey() {
      return `field.identifier.${this.$route.params.id}`
    },

    isChargingPoint() {
      return this.order?.kind === 'charging-point'
    },

    takenByMe() {
      return !!this.currentUser?.id && this.order?.technician?.id === this.currentUser.id
    },

    takenByOther() {
      return !!this.order?.technician && !this.takenByMe
    },

    isOpen() {
      return this.order?.status === 'open' || this.order?.status === 'pending-approval'
    },

    /** Le moment de poser : la sienne, ou une libre et prête (la mise en service la lui donne). */
    installing() {
      return this.isOpen && !!this.order.installation && !this.takenByOther && (this.takenByMe || this.order.readyForInstallation)
    },

    /** Signaler un problème : sur la sienne, ou sur une libre qu'il a sous les yeux. */
    canSignal() {
      return this.isOpen && !!this.order.installation && !this.takenByOther
    },

    installationColor() {
      return { 'to-schedule': 'grey', scheduled: 'indigo', 'in-progress': 'teal' }[this.order?.installation] || 'grey'
    },

    contact() {
      return this.contactOf(this.order)
    },

    phone() {
      return this.contact?.phone || null
    },

    /** L'emplacement prévu par la demande, en mots ; rien s'il n'y en a pas. */
    plannedLocation() {
      const o = this.order
      if (!o) return null
      // L'adresse postale de la personne ne vaut emplacement que si la politique lui demande où poser : imposé, c'est
      // l'emplacement de la commande ; au choix du technicien, rien n'est prévu.
      const personAddress = o.holder === 'member' && (o.locationChoice || 'person') === 'person' && o.person?.postalAddress
      if (o.locationId || o.newLocation || personAddress) {
        return this.addressOf(o)
      }
      return null
    },

    locationReady() {
      return this.correcting || !this.plannedLocation ? !!(this.newPlace?.locationId || this.newPlace?.newLocation?.street) : true
    },

    canCheck() {
      const equipmentKnown = this.identifier.trim() !== '' || !!this.order?.preparedEquipmentId
      return this.locationReady && equipmentKnown && !!this.order?.readyForInstallation
    },

    failedStep() {
      return (this.plan?.steps || []).find(step => step.outcome === 'failed') || null
    },

    /**
     * Le geste suivant, selon l'état : on désigne l'équipement (saisir puis vérifier), puis on met en service (après
     * avoir confirmé une borne déjà affectée) ; un plan arrêté se reprend. Rien à prendre ni à commencer avant.
     */
    action() {
      if (!this.order || !this.installing) return null
      if (this.plan && this.plan.status !== 'completed') {
        return { key: 'retry', icon: 'mdi-refresh', run: () => this.activate(this.plannedOnSite) }
      }
      if (this.preview) {
        return {
          key: 'commission',
          icon: 'mdi-flash',
          run: () => this.activate({ ...this.onSiteBody, evseReferences: this.evseReferences, confirmReassignment: !!this.preview.reassignment && this.reassignmentConfirmed }),
          disabled: !!this.preview.reassignment && !this.reassignmentConfirmed,
        }
      }
      return { key: 'check', icon: 'mdi-magnify', run: this.check, disabled: !this.canCheck }
    },

    hasBar() {
      return !!this.action || this.canSignal || (!!this.error && !!this.order && this.order.status !== 'in-service')
    },

    /** Ce qui est dit sur place, en POST. */
    onSiteBody() {
      const value = this.identifier.trim()
      // Le compteur se désigne par son identifiant ou sa référence (n° de série, PRM) : le serveur résout.
      const body = value ? (this.isChargingPoint ? { identifier: value } : { meterDeviceId: value }) : {}
      if (this.newPlace?.locationId) return { ...body, locationId: this.newPlace.locationId }
      return this.newPlace?.newLocation ? { ...body, newLocation: this.newPlace.newLocation } : body
    },

    /** Reprendre un plan arrêté : le même équipement, relu dans le geste qui le porte. */
    plannedOnSite() {
      const params = (this.plan?.steps || []).map(step => step.params || {})
      const identifier = params.find(p => p.identifier)?.identifier
      const meterDeviceId = params.find(p => p.meterDeviceId)?.meterDeviceId
      return identifier ? { identifier } : (meterDeviceId ? { meterDeviceId } : this.onSiteBody)
    },

    routeUrl() {
      const address = this.plannedLocation
      return address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}` : null
    },
  },

  watch: {
    identifier(value) {
      this.remember(value || '')
    },

    // La vérification valait pour l'emplacement d'alors : changé, elle se refait (et la confirmation d'une borne
    // affectée ailleurs avec elle).
    newPlace: {
      deep: true,
      handler() {
        this.forgetPreview()
      },
    },
    correcting() {
      this.forgetPreview()
    },

    // « Pose suivante » ouvre une autre fiche dans le même écran.
    '$route.params.id'() {
      this.reset()
      this.load()
    },
  },

  created() {
    this.reset()
    this.load()
  },

  methods: {
    /** Le site choisi dans la liste : l'emplacement existant, qu'on retient tel quel. */
    chooseSite(id) {
      const site = this.sites.find(s => s.id === id)
      this.newPlace = site ? { locationId: site.id, locationName: site.name, address: site.address } : null
    },

    isSite(id) {
      return this.sites.some(s => s.id === id)
    },

    siteText(site) {
      const a = site.address || {}
      return [site.name, [a.number, a.street].filter(Boolean).join(' '), [a.zipcode, a.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')
    },

    forgetPreview() {
      if (this.preview && !this.plan) {
        this.preview = null
        this.reassignmentConfirmed = false
      }
    },

    reset() {
      Object.assign(this, { order: null, plan: null, preview: null, newPlace: null, correcting: false, error: null, issueOpen: false, reassignmentConfirmed: false, evseReferences: {}, sites: [] })
      this.identifier = this.recall()
    },

    async load() {
      await this.run(async () => {
        this.order = await FieldRepository.order(this.$route.params.id)
        // Les autres emplacements possibles, sans attendre (un échec n'empêche pas de saisir une adresse).
        FieldRepository.locations(this.order.id).then(sites => { this.sites = sites || [] }).catch(() => { this.sites = [] })
        this.plan = this.order.plan || null
        // Une borne posée se suit sur sa fiche : c'est là qu'on la teste.
        if (this.order.status === 'in-service' && this.isChargingPoint && this.order.equipmentId) {
          this.toChargingPoint(false)
        }
      })
    },

    /** La fiche de la borne posée ; juste après la pose, avec ce qu'il reste à vérifier avant de partir. */
    toChargingPoint(justCommissioned) {
      this.$router.replace({
        name: 'field.charging-point',
        params: { id: this.order.equipmentId },
        // La commande suit : l'intervention sur la borne est sa pose.
        query: justCommissioned ? { commissioned: '1', order: this.order.id } : { order: this.order.id },
      })
    },

    check() {
      return this.run(async () => {
        this.reassignmentConfirmed = false
        this.preview = await FieldRepository.activationPreview(this.order.id, this.asQuery(this.onSiteBody))
        // Ce que la borne porte déjà, à compléter ou corriger.
        this.evseReferences = Object.fromEntries((this.preview.equipment?.evses || []).map(evse => [evse.id, evse.physicalReference || '']))
      })
    },

    activate(onSite) {
      return this.run(async () => {
        const result = await FieldRepository.activate(this.order.id, onSite)
        this.plan = result.plan
        this.preview = null
        this.order = { ...this.order, ...result.order }
        if (result.plan.status === 'completed') {
          this.remember('')
          if (this.isChargingPoint && this.order.equipmentId) {
            this.toChargingPoint(true)
            return
          }
        }
        window.scrollTo(0, 0)
      })
    },

    /** La pose suivante : la prochaine des siennes, sinon la première à prendre, sinon la liste. */
    next() {
      return this.run(async () => {
        const orders = await FieldRepository.orders()
        const following = [...orders.mine, ...orders.open].find(o => o.id !== this.order.id && o.status !== 'in-service')
        this.$router.push(following ? { name: 'field.order', params: { id: following.id } } : { name: 'field' })
      })
    },

    /** Rendue ou signalée : elle n'est plus la sienne, on revient à la liste avec le mot de ce qui s'est passé. */
    leave(notice) {
      this.remember('')
      this.$router.replace({ name: 'field', query: { notice } })
    },

    stepLabel(step) {
      return planStepName(this, step)
    },

    translated(key, params) {
      return this.$te(key) ? this.$t(key, params || {}) : key
    },

    async run(action) {
      this.working = true
      this.error = null
      try {
        await action()
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline')
      } finally {
        this.working = false
      }
    },

    remember(value) {
      try {
        if (value) {
          localStorage.setItem(this.storageKey, value)
        } else {
          localStorage.removeItem(this.storageKey)
        }
      } catch (e) {
        // Stockage indisponible (navigation privée) : on perd seulement le confort de la reprise.
      }
    },

    recall() {
      try {
        return localStorage.getItem(this.storageKey) || ''
      } catch (e) {
        return ''
      }
    },
  },
}
</script>

<style scoped>
.field-order--with-bar {
  padding-bottom: 88px;
}
.field-order__lines > div {
  display: flex;
  align-items: center;
  margin-top: 4px;
}
.field-order__step {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 0.95rem;
  margin-bottom: 8px;
}
.field-order__quick {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}
.field-order__quick >>> .v-btn {
  width: 100%;
  min-width: 0 !important;
  padding: 0 6px !important;
  font-size: 0.75rem;
  letter-spacing: 0;
  text-transform: none;
}
.field-order__identifier >>> input {
  font-size: 1rem;
  letter-spacing: 0.03em;
}
.field-order__bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  background: #fff;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
}
.field-order__bar-inner {
  max-width: 720px;
  margin: 0 auto;
}
.field-order__problem {
  min-width: 44px !important;
  padding: 0 !important;
}
.field-order__main {
  flex: 1 1 0;
  min-width: 0 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0;
}
</style>
